<template>
  <!-- Trip table list -->
  <div class="invoice-import-trips" :class="{ hiddentable: !displayTable }">
    <div class="import-filters">

      <FilterBar 
        :cookieName="filterCookieName" 
        filterBarType="trips" 
        @change="filtersChanged" 
        :mainFilters="['DateRangeFilter', 'ImportCustomerFilter', 'TripTypeFilter']"
        class="w-100"
      >
        <template v-slot:right-bar>
          <div class="" style="display:flex; gap: 0.5rem; align-items: center;">
            <div class="" style="display:flex; gap: 0.5rem; align-items: center;">
              {{ $gettext('Aggregate items?') }}

              <font-awesome-icon id="tooltip-aggregate" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
              <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-aggregate" triggers="hover">
                <div class="tooltip-title">
                  {{ $gettext('You can aggregate your trips in the following ways:') }}
                </div>
                <div class="tooltip-text">
                  {{ $gettext('All: will import all your trips into a single invoice line') }}<br>
                  {{ $gettext('No grouping: will import all trips in separate lines.') }}
                </div>
              </b-tooltip>

              <ImportAggregateTrips v-model="aggregationType" />
            </div>
            <div class="" style="display:flex; gap: 0.5rem; align-items: center;">
              {{ $gettext('Invoice as:') }}
              <ImportInvoiceAsTrips v-model="invoiceAsKm" />
            </div>
          </div>
        </template>
      </FilterBar>
      
    </div>
    <div class="selected-items-wrap">
      <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }">

        <b-table 
          :items="groupedItems"
          :fields="selectedFields"
          class="import-selected-items-table"
          :class="['gk-table gk-table--responsive gk-table--headed gk-table--striped']"
          show-empty
        >
          <template #empty="">
            {{ $gettext('No items selected to import')}}
          </template>
          <template #cell(description)="row">
            {{ row.item.description }}
          </template>
          <template #cell(invoice_as)="row">
            {{ row.item.multiplier | formatNumberDecimal(currentCompany.hours_rounding_precision) }}
          </template>
          <template #cell(compensation)="row">
            <span class="gk-input-light">
              <b-input-group prepend="€" class="icon-contained-input-group">
                <b-form-input v-model="row.item.amount" type="number" required />
              </b-input-group>
            </span>
          </template>
          <template #cell(total)="row">
            <span class="text-nowrap">
              € {{ (row.item.multiplier * row.item.amount) | formatNumber }}
            </span>
          </template>
        </b-table>

      </overlay-scrollbars>
    </div>
    <div class="import-actions-bar">
      <GekkoButton 
        @click="displayTable = !displayTable"
        :text="$gettext('Selected trips') + ' ' + '(' + selectedTrips.length + '/' + itemCount + ')'"
        :icon="(displayTable) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
        icon-right
        variant="link"
        class="px-0"
      />
      <div class="import-actions-bar--buttons">
        <div style="margin-right: 1rem; font-size: 14px;">
          {{ $gettext('Import a total of') }} {{ groupedItems.length }} {{ $gettext('lines') }}
        </div>
        <GekkoButton 
          @click="$emit('close', false)"
          :text="$gettext('Cancel')"
          variant="light"
        />
        <GekkoButton 
          @click="$emit('add-lines', groupedItems)"
          :text="(isQuotation) ? $gettext('Add to quotation') : $gettext('Add to invoice')"
        />
      </div>
    </div>
    <div class="available-items-wrap">
      <TripTable 
        ref="trip-table"
        :filter-bar="false"
        :component-on-modal="true" 
        :modal-temp-cookie="filterCookieName"
        :selectable-rows="true"
        :excludeTableFields="['created_by', 'completed']"
        :overlay-scrollb="false"
        @selected-item="selectedTripTableItems"
        @mounted="filtersChanged" 
        @destroyed="tripTableDestroyed"
        @get-trips="determineCheckedTrips"
        extended
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import FilterBar from "@/components/list_filters/FilterBar"
import GekkoButton from "@/components/GekkoButton";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import TripTable from "@/components/trips/TripTable";
import ImportAggregateTrips from "@/components/invoices/ui/ImportAggregateTrips";
import ImportInvoiceAsTrips from "@/components/invoices/ui/ImportInvoiceAsTrips";

export default {
  name: "ImportTripsToInvoice",
  components: {
    GekkoButton,
    LoadingSpinner,
    TripTable,
    FilterBar,
    ImportAggregateTrips,
    ImportInvoiceAsTrips
  },
  props: {
    filterCookieName: {
      type: String,
      default: ''
    },
    invoice: {
      type: Object,
      default: () => {}
    },
    isQuotation: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: true,
      groupedItems: [],
      selectedTrips: [],
      tripList: [],
      itemCount: 0,
      aggregationType: null,
      displayTable: false,
      initFilters: false,
      invoiceAsKm: 'distance',
      defaultKmRate: null,
      tripsHourlyRate: null
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      selectedCustomer: "invoices/getInvoiceCreateRecipient",
      selectedProject: "invoices/getInvoiceCreateProject",
    }),
    selectedFields () {
      return [
        { key: "description", label: this.$gettext('Description'), class: 'w-75' },
        { key: "invoice_as", label: (this.invoiceAsKm === 'distance') ? this.$gettext('Distance') : this.$gettext('Hours'), class: 'w-25 tablecol-sm text-right' },
        { key: "compensation", label: (this.invoiceAsKm === 'distance') ? this.$gettext('KM compensation (Ex. VAT)') : this.$gettext('Hourly rate (Ex. VAT)'), class: 'w-25 tablecol-sm text-right' },
        { key: "total", label: this.$gettext('Total'), class: 'w-25 tablecol-sm text-right' },
      ]
    }
  },
  watch: {
    invoiceAsKm () {
      this.tripsHourlyRate = this.getHourlyRate()
      this.groupBy()
    },
    aggregationType () {
      this.groupBy()
    },
    selectedTrips() {
      this.groupBy()
    }
  },
  created () {
    this.defaultKmRate = (this.selectedCustomer.default_km_rate) ? this.selectedCustomer.default_km_rate : this.currentCompany.default_km_rate
    this.tripsHourlyRate = this.getHourlyRate()

    this.groupBy()
  },
  mounted () {
    const modalEl = document.getElementsByClassName('gk-modal-wrapper')[0]
    modalEl.addEventListener('scroll', this.handleScroll)
  },
  methods: {
    selectedTripTableItems () {
      this.refreshSelectedList()
    },
    tripTableDestroyed () {
      const modalEl = document.getElementsByClassName('gk-modal-wrapper')[0]
      if (modalEl) {
        modalEl.removeEventListener('scroll', this.handleScroll)
      }
    },
    determineCheckedTrips (item_count) {
      this.itemCount = item_count
      this.tripList = this.$refs['trip-table'].tripTableItems

      let invoice_lines = []
      if (!this.isQuotation && this.invoice.invoice_lines) {
        invoice_lines = this.invoice.invoice_lines;
      }
      if (this.isQuotation && this.invoice.quotationline_set) {
        invoice_lines = this.invoice.quotationline_set;
      }

      for (let i = 0; i < this.tripList.length; i++) {
        for (let j = 0; j < invoice_lines.length; j++) {
          if (invoice_lines[j].trip_ids && invoice_lines[j].trip_ids.indexOf(this.tripList[i].id) != -1) {
            this.tripList[i].is_invoiced = true
            break;
          }
        }
      }

      for (let i = 0; i < this.tripList.length; i++) {
        if(!this.tripList[i].is_invoiced && this.tripList[i].type === 'business' && this.tripList[i].customer && this.tripList[i].customer.id == this.selectedCustomer.id) {
          this.tripList[i].selected = !this.tripList[i].is_invoiced
        }
      }
      this.refreshSelectedList()
    },
    refreshSelectedList () {
      this.selectedTrips = this.$refs['trip-table'].selectedTableItems
    },
    
    /**
     * Groups items and formats output for invoice lines 
     * invoice lines containing aggregated items
     * should have an array with correspondant item ids
     *    COSTS -> (array) invoice_line.cost_ids
     *    HOURS -> (array) invoice_line.aggregated_hours
     *    TRIPS -> (array) invoice_line.trip_ids
     *    PRODUCTS -> (number) invoice_line.product - cannot be grouped, just the ID
     * 
     * @returns {array} group of items formatted, to be added as invoice lines
     */
    groupBy () {
      const items = this.selectedTrips
      const grouped_items = items.reduce((acc_items, item) => {
        const key = (!this.aggregationType || this.aggregationType === 'no') ? item.id : null

        const existing_group = acc_items.find(group => group.grouped_by?.id === key);

        // Update group values
        if (existing_group) {

          // Increment amounts
          // existing_group.amount += parseFloat(item.amount)
          // existing_group.total_tax += parseFloat(item.total_tax)

          // MULTIPLIER ?
          // vm.grouped_costs[key].multiplier = Math.round(vm.grouped_costs[key].multiplier*100)/100;

          existing_group.multiplier += this.roundFloat((this.invoiceAsKm === 'distance') ? parseFloat(item.total_distance) : parseFloat(item.total_hours))

          // Save item id
          existing_group.trip_ids.push(item.id)

          // Set first and last date for item description
          existing_group.first_date = Math.min(new Date(existing_group.first_date), new Date(item.date));
          existing_group.last_date = Math.max(new Date(existing_group.last_date), new Date(item.date));
          
        } else {
          // Push a new group

          if (!item.start.name) {
            item.start.name = this.$options.filters.formatAddress(item.start)
          }
          if (!item.end.name) {
            item.end.name = this.$options.filters.formatAddress(item.end)
          }

          const group_first_item = {
            grouped_by: { type: this.aggregationType, id: key },
            trip_ids: [item.id],
            multiplier: this.roundFloat((this.invoiceAsKm === 'distance') ? parseFloat(item.total_distance) : parseFloat(item.total_hours)),
            description: this.$gettext('Trip on') + ' ' + (new Date(item.date).toISOString().split('T')[0]) + ': ' + item.start.name + ' - ' + item.end.name,
            amount: (this.invoiceAsKm === 'distance') ? parseFloat(this.defaultKmRate) : parseFloat(this.tripsHourlyRate),
            total_tax: parseFloat(item.total_tax),
            tax_rate: this.currentCompany.default_tax_rate,
            vat_exempt: false,
            first_date: item.date,
            last_date: item.date,
          }
          if (this.aggregationType === 'no' && item.project) {
            group_first_item.project = item.project
          }
          if (!group_first_item.amount) {
            group_first_item.amount = 0
          }
          acc_items.push(group_first_item);
        }

        return acc_items;
      }, []);

      // Format the descriptions
      grouped_items.forEach(grouped_item => {
        grouped_item.first_date = new Date(grouped_item.first_date).toISOString().split('T')[0]
        grouped_item.last_date = new Date(grouped_item.last_date).toISOString().split('T')[0]
        if (this.aggregationType === 'all') {
          grouped_item.description = this.$gettext('Trips between') + ' ' + grouped_item.first_date + ' ' + this.$gettext('and') + ' ' + grouped_item.last_date
        }
        delete grouped_item.first_date;
        delete grouped_item.last_date;
      });

      this.groupedItems = grouped_items;
    },
    roundFloat (input) {
      /**
       * Round the hours entries to 1 decimal (default), 2 or 0;
       */
      let fractionSize = 1
      if (this.currentCompany && this.currentCompany.hours_rounding_precision in [0, 1, 2]) {
        fractionSize = parseInt(this.currentCompany.hours_rounding_precision)
      }
      const roundedInput = parseFloat(input).toFixed(fractionSize)
      return parseFloat(roundedInput)
    },
    getHourlyRate () {
      if (this.selectedProject && this.selectedProject.hourly_rate != null && this.aggregationType === 'no') {
        return this.selectedProject.hourly_rate
      }
      if (this.selectedCustomer.hourly_rate != null) {
        return this.selectedCustomer.hourly_rate
      }
      if (this.currentCompany.hourly_rate != null) {
        return this.currentCompany.hourly_rate
      }
      return 0
    },
    filtersChanged (value) {
      if (value) this.initFilters = value
      if (this.$refs['trip-table']) {
        this.$refs['trip-table'].filtersChanged(this.initFilters)
      }
    },
    handleScroll () {
      this.$refs['trip-table'].handleScroll()
    },
  }
};
</script>

<style lang="scss" scoped>

.invoice-import-trips {
  display: flex;
  flex-direction: column;
  // gap: 1rem;
  
  .import-filters {
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding-top: 0.5rem;
  }
  .import-actions-bar {
    display: flex;
    justify-content: space-between;
    // gap: 0.5rem;
    align-items: center;
    padding: 1rem 0;
    &--buttons {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      align-items: center;
    }
  }
  .selected-items-wrap {
    height: 350px;
  }
  &.hiddentable {
    // margin-bottom: -1rem;
    .import-actions-bar {
      padding-bottom: 0;
    }
    .available-items-wrap {
        height: 0;
        overflow: hidden;
    }
  }
}

</style>

<style lang="scss">

</style>